import { memo, useMemo } from 'react';

import {
  formatBookingAnswers,
  parseBookingAnswers,
} from '@/lib/crm/utils/bookingQuestions';
import { cx } from '@/utils/cx';

export interface BookingAnswersInlineLabelProps {
  value: unknown;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
  /** Truncate text to this many characters with "…" and show full text on hover */
  maxLength?: number;
}

const fontSizeClasses: Record<string, string> = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Compact, single-line rendering of an answers-snapshot json property
 * (`ahoy_custom_answers` / `ahoy_booking_answers`) for space-constrained
 * contexts — table cells, proposal/action cards, diff tables.
 *
 * @example
 * <BookingAnswersInlineLabel value={booking.properties?.ahoy_custom_answers} />
 * // Renders: "Company size: 50-100; Referral source: Google"
 */
export const BookingAnswersInlineLabel = memo(
  ({
    value,
    fontSize = 'sm',
    className = '',
    truncate,
    maxLength,
  }: BookingAnswersInlineLabelProps) => {
    const sizeClass = fontSizeClasses[fontSize];
    const answers = useMemo(() => parseBookingAnswers(value), [value]);

    if (answers.length === 0) {
      return <span className={cx(sizeClass, className)}>—</span>;
    }

    const text = formatBookingAnswers(answers);

    const truncatedText =
      maxLength !== undefined && text.length > maxLength
        ? text.slice(0, maxLength) + '…'
        : text;

    // `min-w-0` lets this block-level span shrink below its intrinsic content
    // width when it's a flex item; without it `text-ellipsis` has no
    // smaller-than-content width to clip against and long answers overflow.
    const truncateClasses = truncate
      ? 'block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap'
      : '';

    const showTitle =
      truncate || (maxLength !== undefined && text.length > maxLength);

    return (
      <span
        className={cx(sizeClass, truncateClasses, className)}
        title={showTitle ? text : undefined}
      >
        {truncatedText}
      </span>
    );
  }
);
BookingAnswersInlineLabel.displayName = 'BookingAnswersInlineLabel';
